/**
 * One glossary for the whole product (docs/PATH-V2.md §3.2): the words a child of stages 1–2 hears for «a piece can
 * take», «guarded», «a gift», «under attack» and «a trade», and the words he must not hear instead.
 *
 * It is applied as a FILTER, never by rewriting text: the recorded voice keys a take by pool + 1-based wording number,
 * so an existing wording is never edited. `lint.ts` checks new path content strictly and reports the old pools;
 * `book.ts` prefers an allowed wording when it chooses (mode 'prefer'), and takes the old one when no allowed recorded
 * wording fits — Гамбитик never goes silent because of the glossary.
 *
 * Matching is on the lower-cased, ё→е text with «quoted» names blanked (the same length as the original, so a match
 * maps back to the original letters), every stem anchored at a word start.
 */

export type GlossaryConcept = 'reach' | 'guard' | 'gift' | 'danger' | 'trade';

/** A forbidden word of a concept: a regex over the lower-cased, ё→е text (global flag added by the matcher). */
export interface GlossaryBan {
  /** stable id of the rule («stem») — reports and exceptions name it */
  id: string;
  re: RegExp;
  /** the ban holds at stages ≤ this (§3.2: «на ступенях 1–2 нельзя») */
  untilStage: number;
  /** only for the new path pools (`v3.path.*` / `v3.lesson.*`): «размен» before chapter 1.4 */
  pathOnly?: boolean;
}

export interface GlossaryEntry {
  concept: GlossaryConcept;
  /** what the child hears (§3.2 «Слово») */
  allowed: readonly string[];
  /** the board sign that goes with the word */
  sign: string;
  forbidden: readonly GlossaryBan[];
}

/** Start of a word: not after a Cyrillic letter. */
const W = '(?<![а-я])';
/** End of a word: not before a Cyrillic letter. */
const E = '(?![а-я])';
const re = (src: string): RegExp => new RegExp(src, 'u');

export const GLOSSARY: readonly GlossaryEntry[] = [
  {
    concept: 'reach',
    allowed: ['достаёт'],
    sign: 'жёлтая линия',
    forbidden: [
      { id: 'бьёт клетки', re: re(`${W}(?:бить|бьет|бьют|будет бить)\\s+(?:[а-я-]+\\s+){0,2}клет`), untilStage: 2 },
      { id: 'стреляет', re: re(`${W}(?:стреля|стрельн|стрельб|обстрел)`), untilStage: 2 },
      // «нападает на фигуру» is allowed (also «нападёт сразу на двоих», «на коня напали»); «нападает» alone,
      // «нападение», «нападающий» are not
      {
        id: 'нападает',
        re: re(`(?<!${W}на\\s+(?:[а-я-]+\\s+){0,2})${W}(?:напад[а-я]*|напасть|напал[а-я]*)(?![а-я])(?!\\s+(?:[а-я]+\\s+)?на${E})`),
        untilStage: 2,
      },
    ],
  },
  {
    concept: 'guard',
    allowed: ['охраняет', 'без охраны', 'поставь охрану'],
    sign: 'синий щит',
    forbidden: [
      { id: 'стражи', re: re(`${W}страж`), untilStage: 2 },
      // «часовые / часового» (sentries); never «часов» (hours), «часовая стрелка», «по часовой стрелке»
      { id: 'часовые', re: re(`${W}часов(?:ой|ого|ому|ым|ом|ые|ых|ыми)${E}(?!\\s+стрелк)`), untilStage: 2 },
      { id: 'прикрывает', re: re(`${W}(?:прикрыва|прикрыт|прикрыл|прикро|прикрой)`), untilStage: 2 },
      { id: 'сторожит', re: re(`${W}сторож`), untilStage: 2 },
      // «без защиты» / «незащищённая» belong to the gift concept (below); here the verb and the noun
      { id: 'защищает', re: re(`${W}(?<!без )(?:защищ|защит)`), untilStage: 2 },
    ],
  },
  {
    concept: 'gift',
    allowed: ['подарок', 'даром'],
    sign: '🎁 над фигурой',
    forbidden: [
      // «висит» is said once, in chapter 1.4, as «a chess word» — a dedicated pool allows it (GLOSSARY_POOL_ALLOW)
      { id: 'висит', re: re(`${W}(?:по)?(?:вис(?:ит|ят|ел|ела|ело|ели|еть|ла|ло|ли|нет|нут|ящ[а-я]*)|висяч[а-я]*)${E}`), untilStage: 2 },
      { id: 'бесплатно', re: re(`${W}бесплатн`), untilStage: 2 },
      { id: 'просто так', re: re(`${W}просто\\s+так${E}`), untilStage: 2 },
      { id: 'без защиты', re: re(`${W}без\\s+защит`), untilStage: 2 },
      { id: 'незащищённая', re: re(`${W}незащищ`), untilStage: 2 },
    ],
  },
  { concept: 'danger', allowed: ['под ударом'], sign: 'красная точка', forbidden: [] },
  {
    concept: 'trade',
    allowed: ['мы тоже отдаём'],
    sign: '—',
    // «размен» before chapter 1.4: the path's stage 1 (the old pools keep their recorded «размен»; chapter 1.4's own
    // pools introduce the word — GLOSSARY_POOL_ALLOW)
    forbidden: [{ id: 'размен', re: re(`${W}(?:размен|разменя)`), untilStage: 1, pathOnly: true }],
  },
];

/**
 * Pools that may use a forbidden word (by rule id):
 * - «висит» as «a chess word», said once in chapter 1.4 «Размен» (the beat `v3.lesson.hanging-word.say`);
 * - «размен» from chapter 1.4 on — in that chapter's own pools: its lessons `v3.lesson.trade-*`, its instructions
 *   `v3.path.trade.*`, its map intro, praise and hint. Shared pools (`v3.path.why.*` …) keep the stage-1 ban.
 */
export const GLOSSARY_POOL_ALLOW: readonly { pool: RegExp; rules: readonly string[] }[] = [
  { pool: /^v3\.lesson\.hanging-word\./u, rules: ['висит'] },
  { pool: /^v3\.(?:lesson\.trade-[a-z0-9-]+\.|path\.(?:trade|chapter\.trade)\.|path\.(?:praise|hint)\.trade$)/u, rules: ['размен'] },
];

/** The new path content: `v3.path.*` and `v3.lesson.*` (docs/PATH-V2.md §8.2) — the glossary is strict there. */
export function isPathPool(pool: string): boolean {
  return pool.startsWith('v3.path.') || pool.startsWith('v3.lesson.');
}

export interface GlossaryOptions {
  /** the pool the text belongs to: path pools add the path-only bans, a pool may be allowed some rules */
  pool?: string;
  /** treat as a path text even without a pool id */
  path?: boolean;
}

export interface GlossaryViolation {
  /** the forbidden word as written in the text */
  word: string;
  concept: GlossaryConcept;
  /** the ban's id («висит», «защищает», …) */
  rule: string;
}

/**
 * Lower-cased, ё→е, and every «quoted» stretch blanked out (same length): a quoted name is a name, not the word —
 * «Французская защита», «Защита двух коней», «висит» said as «a chess word».
 */
function normalize(text: string): string {
  return text
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/«[^»]*»/gu, (q) => ' '.repeat(q.length));
}

/** The forbidden words of the glossary in `text` for a child at `stage` (word-boundary safe, case- and ё-insensitive). */
export function glossaryViolations(text: string, stage: number, opts: GlossaryOptions = {}): GlossaryViolation[] {
  const plain = normalize(text);
  const path = opts.path === true || (opts.pool !== undefined && isPathPool(opts.pool));
  const allowedRules = new Set(opts.pool ? GLOSSARY_POOL_ALLOW.filter((a) => a.pool.test(opts.pool as string)).flatMap((a) => a.rules) : []);
  const out: GlossaryViolation[] = [];
  for (const entry of GLOSSARY) {
    for (const ban of entry.forbidden) {
      if (stage > ban.untilStage) continue;
      if (ban.pathOnly && !path) continue;
      if (allowedRules.has(ban.id)) continue;
      const g = new RegExp(ban.re.source, 'gu');
      for (const m of plain.matchAll(g)) {
        const at = m.index ?? 0;
        // the whole last word: «защищ» → «защищает»
        let end = at + m[0].length;
        while (end < plain.length && /[а-я]/u.test(plain[end] as string)) end++;
        out.push({ word: text.slice(at, end), concept: entry.concept, rule: ban.id });
      }
    }
  }
  return out;
}

/** Does the text keep the glossary for this stage? */
export function glossaryOk(text: string, stage: number, opts: GlossaryOptions = {}): boolean {
  return glossaryViolations(text, stage, opts).length === 0;
}

/** «подарок / подарить / даром» (any form; «удар», «ударом» are not it). */
const GIFT_WORD_RE = new RegExp(`${W}(?:подар|даром${E})`, 'u');

/**
 * Does the text call something a gift? §3.2: «Маты и вилки подарками не называются ни в пути, ни в партиях» — a moment
 * that is a mate or a tactic (not an `isGift` capture of analysis/gifts.ts) picks only wordings where this is false
 * (`PickArgs.notGift` of ./book.ts). Any stage: the rule is about the meaning, not the child's age.
 */
export function callsGift(text: string): boolean {
  return GIFT_WORD_RE.test(normalize(text));
}
